import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Stack,
  Divider,
} from '@mui/material';
import { CheckCircle, Home, Store } from '@mui/icons-material';
import { useTranslation } from '@/hooks/useTranslation';

interface SuccessState {
  orderId?: string;
  amount?: number;
  currency?: string;
  credits?: number;
  packageName?: string;
}

const CheckoutSuccessPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as SuccessState | undefined;
  const purchaseTrackedRef = useRef(false);

  useEffect(() => {
    if (state?.orderId && !purchaseTrackedRef.current) {
      purchaseTrackedRef.current = true;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'purchase',
        ecommerce: {
          transaction_id: state.orderId,
          value: state.amount,
          currency: state.currency || 'EUR',
          items: [
            {
              item_name: state.packageName,
              price: state.amount,
              quantity: 1,
            },
          ],
        },
      });
    }
  }, [state]);

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', textAlign: 'center' }}>
      <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2, mt: 4 }} />

      <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
        {t('checkoutSuccess.title')}
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('checkoutSuccess.subtitle')}
      </Typography>

      {state?.orderId && (
        <Card sx={{ mb: 4, textAlign: 'left' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('checkoutSuccess.orderDetails')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">
                  {t('checkoutSuccess.orderId')}
                </Typography>
                <Typography fontWeight={600}>{state.orderId}</Typography>
              </Box>
              {state.packageName && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">
                    {t('checkoutSuccess.package')}
                  </Typography>
                  <Typography fontWeight={600}>{state.packageName}</Typography>
                </Box>
              )}
              {state.credits != null && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">
                    {t('checkoutSuccess.credits')}
                  </Typography>
                  <Typography fontWeight={600}>{state.credits}</Typography>
                </Box>
              )}
              {state.amount != null && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">
                    {t('checkoutSuccess.amount')}
                  </Typography>
                  <Typography fontWeight={600} color="primary">
                    €{state.amount}
                  </Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
        <Button
          variant="contained"
          size="large"
          startIcon={<Home />}
          onClick={() => navigate('/dashboard')}
        >
          {t('checkoutSuccess.goToDashboard')}
        </Button>
        <Button
          variant="outlined"
          size="large"
          startIcon={<Store />}
          onClick={() => navigate('/shop')}
        >
          {t('checkoutSuccess.goToShop')}
        </Button>
      </Stack>
    </Box>
  );
};

export default CheckoutSuccessPage;
